import { createSlice } from '@reduxjs/toolkit';
import { UserType } from '../../types/user/user.type';
import { FETCH_STATUS } from '../../types/enums/fetch-status.enum';
import {
  getCurrentUser,
  logout,
  postForgotPassword,
  postLogin,
  postResetPassword,
  switchOrganization,
} from './auth.action';
import { patchUserDetails } from '../users/users.action';
import { agreementType } from '../../utils/constants';
import { history } from '../../utils/utilities';
import { AGREEMENT_TYPE } from '../../types/enums/agreement-type.enum';
import NetworkService from '../../utils/network.service';
import snackbarUtils from '../../utils/SnackbarUtils';
import { setOktaLoginSource } from '../okta/okta.config';

export interface AuthState {
  user: null | UserType;
  userFetchStatus: FETCH_STATUS;
  tokenChecked: boolean;
  loginPostStatus: FETCH_STATUS;
  forgotPasswordPostStatus: FETCH_STATUS;
  resetPasswordPostStatus: FETCH_STATUS;
  redirectFetchStatus: FETCH_STATUS;
  userAccessList: Array<{ id: number; organization: { name: string; id: number } }>;
  logoutStatus: FETCH_STATUS;
  organizationSwitch: {
    isLoading: boolean;
    error: string | null;
  };
}

const INIT_STATE: AuthState = {
  user: null,
  userFetchStatus: FETCH_STATUS.NULL,
  tokenChecked: false,
  loginPostStatus: FETCH_STATUS.NULL,
  forgotPasswordPostStatus: FETCH_STATUS.NULL,
  resetPasswordPostStatus: FETCH_STATUS.NULL,
  redirectFetchStatus: FETCH_STATUS.NULL,
  userAccessList: [],
  logoutStatus: FETCH_STATUS.NULL,
  organizationSwitch: {
    isLoading: false,
    error: null,
  },
};

export const authSlice = createSlice({
  name: 'auth',
  initialState: INIT_STATE,
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload;
    },
    setUserFetchStatus: (state, action) => {
      state.userFetchStatus = action.payload;
    },
    setTokenChecked: (state, action) => {
      state.tokenChecked = action.payload;
    },
    setLoginPostStatus: (state, action) => {
      state.loginPostStatus = action.payload;
    },
    setForgotPasswordPostStatus: (state, action) => {
      state.forgotPasswordPostStatus = action.payload;
    },
    setResetPasswordPostStatus: (state, action) => {
      state.resetPasswordPostStatus = action.payload;
    },
    setRedirectFetchStatus: (state, action) => {
      state.redirectFetchStatus = action.payload;
    },
    setUserAccessList: (state, action) => {
      state.userAccessList = action.payload;
    },
    setOrganizationSwitchLoading: (state, action) => {
      state.organizationSwitch.isLoading = action.payload;
      if (action.payload) {
        state.organizationSwitch.error = null;
      }
    },
    setOrganizationSwitchError: (state, action) => {
      state.organizationSwitch.error = action.payload;
      state.organizationSwitch.isLoading = false;
    },
  },
  extraReducers: builder => {
    // GET CURRENT USER
    builder.addCase(getCurrentUser.pending, state => {
      state.userFetchStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(getCurrentUser.fulfilled, (state, action) => {
      state.user = action.payload.user;
      if (action.payload.init) {
        state.tokenChecked = true;
      }

      setTimeout(() => {
        if (
          !action.payload.init &&
          window.location.pathname.indexOf('/msa') !== -1 &&
          action.payload.user.agreements
            ?.map(a => a.type)
            .includes(<AGREEMENT_TYPE>agreementType.MASTER_SERVICES_AGREEMENT)
        ) {
          history.push('/');
        }

        if (window.location.pathname.indexOf('/authentication') !== -1) {
          history.push('/');
        }
      });

      state.userFetchStatus = FETCH_STATUS.FULFILLED;
    });
    builder.addCase(getCurrentUser.rejected, (state, action) => {
      state.user = null;
      state.userFetchStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });

    builder.addCase(patchUserDetails.fulfilled, (state, action) => {
      if (state.user && state.user.id === action.payload.id) {
        const { organization, ...userDetails } = action.payload;
        state.user = { ...state.user, ...userDetails };
      }
    });

    // POST LOGIN
    builder.addCase(postLogin.pending, state => {
      state.loginPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postLogin.fulfilled, (state, action) => {
      localStorage.setItem('token', action.payload.access_token);
      setOktaLoginSource(null);
      NetworkService.setupDefaultHeaders(action.payload.access_token);
      state.tokenChecked = true;
      state.loginPostStatus = FETCH_STATUS.FULFILLED;
    });

    builder.addCase(postLogin.rejected, (state, action) => {
      state.user = null;
      state.loginPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });

    // POST FORGOT PASSWORD
    builder.addCase(postForgotPassword.pending, state => {
      state.forgotPasswordPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postForgotPassword.fulfilled, state => {
      setTimeout(() => {
        snackbarUtils.success('Check your e-mail for reset password link');
      });
      state.forgotPasswordPostStatus = FETCH_STATUS.FULFILLED;
    });
    builder.addCase(postForgotPassword.rejected, (state, action) => {
      state.forgotPasswordPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });

    // POST RESET PASSWORD
    builder.addCase(postResetPassword.pending, state => {
      state.resetPasswordPostStatus = FETCH_STATUS.PENDING;
    });
    builder.addCase(postResetPassword.fulfilled, state => {
      setTimeout(() => {
        snackbarUtils.success('Password has been changed. Now you can log in using your new password');
      });
      state.resetPasswordPostStatus = FETCH_STATUS.FULFILLED;
    });
    builder.addCase(postResetPassword.rejected, (state, action) => {
      state.resetPasswordPostStatus = FETCH_STATUS.REJECTED;
      setTimeout(() => {
        snackbarUtils.error(action.payload as string);
      });
    });

    // SWITCH ORGANIZATION
    builder.addCase(switchOrganization.fulfilled, (state, action) => {
      state.user = action.payload.user;
      state.tokenChecked = true;
      state.organizationSwitch.isLoading = false;
      setTimeout(() => {
        const organizationName = action.payload.user?.organization?.name || 'organization';
        snackbarUtils.success(`Successfully switched to ${organizationName}`);
      }, 100);
    });
    builder.addCase(switchOrganization.rejected, (state, action) => {
      state.organizationSwitch.isLoading = false;
      state.organizationSwitch.error = action.payload as string;
      setTimeout(() => {
        snackbarUtils.error((action.payload as string) || 'Failed to switch organization');
      }, 100);
    });

    // LOGOUT
    builder.addCase(logout.fulfilled, state => {
      state.user = null;
      state.tokenChecked = false;
      state.userFetchStatus = FETCH_STATUS.NULL;
      state.loginPostStatus = FETCH_STATUS.NULL;
      state.forgotPasswordPostStatus = FETCH_STATUS.NULL;
      state.resetPasswordPostStatus = FETCH_STATUS.NULL;
      state.redirectFetchStatus = FETCH_STATUS.NULL;
      state.userAccessList = [];
      state.organizationSwitch = {
        isLoading: false,
        error: null,
      };
    });
  },
});

export const {
  setUser,
  setForgotPasswordPostStatus,
  setLoginPostStatus,
  setResetPasswordPostStatus,
  setTokenChecked,
  setRedirectFetchStatus,
  setUserAccessList,
  setOrganizationSwitchLoading,
  setOrganizationSwitchError,
} = authSlice.actions;

// Selectors
export const selectIsOrganizationSwitching = (state: { authReducer: AuthState }) =>
  state.authReducer.organizationSwitch.isLoading;

export default authSlice.reducer;
